import {Injectable, inject, signal} from '@angular/core';
import {HttpClient, HttpDownloadProgressEvent, HttpErrorResponse, HttpEventType} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {environment} from '../../../environments/environment';

export type ChatState = 'icon' | 'panel' | 'full';

export interface ToolStep {
  name: string;
  state: 'running' | 'ok' | 'failed';
}

export interface ChatEntry {
  role: 'user' | 'assistant';
  text: string;
  /** ISO timestamp. */
  at: string;
  tools: ToolStep[];
  submissionId?: string;
  error?: string;
  pending?: boolean;
}

const STORAGE_KEY = 'tap-helper-transcript';
const MAX_SAVED = 100;
const HIDDEN_KEY = 'tap-helper-hidden';

/**
 * TAP Helper's conversation and window state. Root-provided so a reply keeps streaming across
 * minimise and navigation. Drives POST /clcta/assistant/message/stream; the response is server-sent
 * events read from the HttpClient's download progress, so the MSAL interceptor still adds the token
 * (EventSource cannot send it). History lives in the backend session; the transcript is mirrored to
 * sessionStorage so it survives a reload.
 */
@Injectable({providedIn: 'root'})
export class TapHelperService {
  private http = inject(HttpClient);

  readonly state = signal<ChatState>('icon');
  readonly entries = signal<ChatEntry[]>(this.restore());
  readonly busy = signal(false);
  readonly elapsed = signal(0);
  readonly unread = signal(false);
  /** User hid the corner launcher; persists until they reopen from the sidebar. */
  readonly hidden = signal(this.readHidden());
  /** Bumped to ask the window to focus its composer. */
  readonly focusTick = signal(0);

  private readonly url = `${environment.tenantSettings.baseUrl}/clcta/assistant`;
  private request?: Subscription;
  private timer?: ReturnType<typeof setInterval>;

  open(): void {
    if (this.state() === 'icon') {
      this.state.set('panel');
    }
    this.unread.set(false);
    this.focusTick.update(n => n + 1);
  }

  /** Sidebar entry: full screen, and brings the launcher back if hidden. */
  openFull(): void {
    this.setHidden(false);
    this.state.set('full');
    this.unread.set(false);
    this.focusTick.update(n => n + 1);
  }

  minimise(): void {
    this.state.set('icon');
  }

  hide(): void {
    this.setHidden(true);
    this.state.set('icon');
  }

  toggleFull(): void {
    this.state.set(this.state() === 'full' ? 'panel' : 'full');
  }

  exitFull(): void {
    if (this.state() === 'full') {
      this.state.set('panel');
    }
  }

  /** False when nothing was sent: busy or blank. */
  send(input: string): boolean {
    const text = input.trim();
    if (!text || this.busy()) {
      return false;
    }

    const at = new Date().toISOString();
    this.entries.update(e => [...e,
      {role: 'user', text, at, tools: []},
      {role: 'assistant', text: '', at, tools: [], pending: true}]);
    this.save();
    this.start();

    let consumed = 0;
    this.request = this.http.post(`${this.url}/message/stream`, {text}, {observe: 'events', reportProgress: true, responseType: 'text'})
      .subscribe({
        next: ev => {
          const body = ev.type === HttpEventType.DownloadProgress ? (ev as HttpDownloadProgressEvent).partialText
            : ev.type === HttpEventType.Response ? ev.body : undefined;
          if (!body) {
            return;
          }
          // Only whole events: each ends with a blank line.
          const end = body.lastIndexOf('\n\n');
          if (end < consumed) {
            return;
          }
          body.substring(consumed, end).split('\n\n').forEach(block => this.handle(block));
          consumed = end + 2;
        },
        error: (err: HttpErrorResponse) => {
          this.patchReply(r => ({...r, error: this.errorMessage(err)}));
          this.stop();
        },
        complete: () => this.stop(),
      });
    return true;
  }

  /** New chat: clears the server session, then the transcript. */
  reset(): void {
    if (this.busy()) {
      return;
    }
    this.http.post(`${this.url}/reset`, {}).subscribe({
      next: () => {
        this.entries.set([]);
        this.save();
      },
      error: err => console.error('Assistant reset failed: ', err),
    });
  }

  /** Logout: drops everything client-side without touching the server. */
  clearLocal(): void {
    this.request?.unsubscribe();
    this.stop();
    this.entries.set([]);
    this.unread.set(false);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage unavailable; nothing to clear.
    }
  }

  private handle(block: string): void {
    let event = 'message';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.substring(6).trim();
      } else if (line.startsWith('data:')) {
        data += line.substring(5).trim();
      }
    }
    if (!data) {
      return;
    }

    let payload;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    switch (event) {
      case 'text':
        this.patchReply(r => ({...r, text: r.text + payload.text}));
        break;
      case 'tool':
        this.patchReply(r => ({
          ...r,
          tools: payload.status === 'start'
            ? [...r.tools, {name: payload.name, state: 'running'}]
            : r.tools.map((t, i) => i === r.tools.length - 1 ? {...t, state: payload.ok ? 'ok' : 'failed'} : t),
        }));
        break;
      case 'done':
        this.patchReply(r => ({...r, text: payload.reply, submissionId: payload.createdSubmissionId ?? undefined}));
        break;
      case 'error':
        this.patchReply(r => ({...r, error: payload.message}));
        break;
    }
  }

  private patchReply(change: (reply: ChatEntry) => ChatEntry): void {
    this.entries.update(e => [...e.slice(0, -1), change(e[e.length - 1])]);
  }

  private errorMessage(err: HttpErrorResponse): string {
    let serverError: string | undefined;
    try {
      serverError = JSON.parse(err.error)?.error;
    } catch {
      // Not our RestResponse (e.g. a proxy error page).
    }
    // 503 with a RestResponse body: the backend has no assistant model configured.
    if (err.status === 503 && serverError) {
      return 'TAP Assistant is not set up on this server yet. Please contact your system administrator.';
    }
    return serverError ?? err.message;
  }

  private start(): void {
    this.busy.set(true);
    this.elapsed.set(0);
    this.timer = setInterval(() => this.elapsed.update(s => s + 1), 1000);
  }

  private stop(): void {
    if (!this.busy()) {
      return;
    }
    this.busy.set(false);
    clearInterval(this.timer);
    this.patchReply(r => ({...r, pending: false}));
    this.save();
    if (this.state() === 'icon') {
      this.unread.set(true);
    }
  }

  private save(): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries().slice(-MAX_SAVED)));
    } catch {
      // Quota or storage disabled: the transcript just won't survive a reload.
    }
  }

  private setHidden(hidden: boolean): void {
    this.hidden.set(hidden);
    try {
      localStorage.setItem(HIDDEN_KEY, String(hidden));
    } catch {
      // Storage unavailable: hidden for this tab only.
    }
  }

  private readHidden(): boolean {
    try {
      return localStorage.getItem(HIDDEN_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private restore(): ChatEntry[] {
    try {
      const saved: ChatEntry[] = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]');
      return saved.map(e => e.pending ? {...e, pending: false, error: 'Reply interrupted by a page reload.'} : e);
    } catch {
      return [];
    }
  }
}
